import Button, { ButtonDefaultPropType } from 'components/Forms/Button/Button';
import { mobileFirstSizes } from 'components/Theme/mediaQueries';
import { useComponentUpdate } from 'hooks/helpers/UseComponentUpdate';
import { useLatest } from 'hooks/ui/useLatest';
import { useMediaMin } from 'hooks/ui/useMediaMin';
import { ArrowIcon, ArrowUpIcon } from 'public/svg/IconsSvg';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import twMerge from 'utils/twMerge';

export type ButtonWrapperType = {
    buttonAlign?: 'left' | 'center';
};

export type LimitHeightType = {
    mobileHeight: number;
    desktopHeight?: number;
};

export type LimitHeightWrapType = LimitHeightType &
    ButtonWrapperType & {
        isWithButtonIcons?: boolean;
        buttonLabelLess?: string;
        buttonLabelMore: string;
        buttonVariant?: ButtonDefaultPropType['variant'];
        desktopSizeDelimiter?: number;
        increaseParentCounter?: () => void;
        updateDependency?: string | number;
        buttonClassName?: string;
        isDefaultCollapsed?: boolean;
        isDefaultEnabled?: boolean;
    };

const DEFAULT_BREAKPOINT = mobileFirstSizes.vl;

const isTopInViewport = (element: HTMLElement) => element.getBoundingClientRect().top >= 0;

const scrollToElement = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    window.scrollTo({
        top: window.scrollY + rect.top,
        behavior: 'smooth',
    });
};

const LimitHeightWrap: FC<LimitHeightWrapType> = ({
    increaseParentCounter,
    buttonAlign,
    isWithButtonIcons,
    buttonLabelLess,
    buttonLabelMore,
    buttonVariant,
    children,
    desktopHeight,
    desktopSizeDelimiter,
    mobileHeight,
    updateDependency,
    buttonClassName,
    isDefaultCollapsed,
    isDefaultEnabled = true,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(isDefaultCollapsed);
    const [isCollapsible, setIsCollapsible] = useState(isDefaultEnabled);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const isDesktop = useMediaMin('vl');

    const collapsedHeight = isDesktop && desktopHeight !== undefined ? `${desktopHeight}px` : `${mobileHeight}px`;

    const checkShouldBeCollapsible = useCallback(
        (windowWidth: number) => {
            const node = wrapperRef.current;
            if (!node) {
                return false;
            }

            const delimiter = desktopSizeDelimiter ?? DEFAULT_BREAKPOINT;
            const currentLimit = windowWidth <= delimiter ? mobileHeight : desktopHeight ?? mobileHeight;

            return node.scrollHeight > currentLimit;
        },
        [desktopHeight, desktopSizeDelimiter, mobileHeight],
    );

    const latestCheckCollapsible = useLatest(checkShouldBeCollapsible);

    const updateStatus = useCallback(() => {
        setIsCollapsible(latestCheckCollapsible.current(window.innerWidth));
    }, [latestCheckCollapsible]);

    useEffect(() => {
        updateStatus();
        window.addEventListener('resize', updateStatus, { passive: true });

        return () => window.removeEventListener('resize', updateStatus);
    }, [updateStatus]);

    useComponentUpdate(() => {
        updateStatus();
        setIsCollapsed(true);
    }, [updateStatus, updateDependency]);

    const toggleCollapse = useCallback(() => {
        setIsCollapsed((prev) => {
            const nextValue = !prev;
            increaseParentCounter?.();

            if (nextValue && wrapperRef.current) {
                requestAnimationFrame(() => {
                    const node = wrapperRef.current!;

                    if (!isTopInViewport(node)) {
                        scrollToElement(node);
                    }
                });
            }

            return nextValue;
        });
    }, [increaseParentCounter]);

    const isLimitApplied = isCollapsible && isCollapsed;
    const showButton = isCollapsible && (isCollapsed || buttonLabelLess);

    return (
        <>
            <div
                ref={wrapperRef}
                style={isLimitApplied ? { height: collapsedHeight } : undefined}
                className={twMerge(
                    'limit-height',
                    isLimitApplied ? 'overflow-hidden' : 'h-auto overflow-visible after:hidden',
                )}
            >
                {children}
            </div>
            {showButton && (
                <div
                    className={
                        buttonAlign !== 'left' ? 'relative bottom-0 z-two -translate-y-9 text-center' : undefined
                    }
                >
                    <Button
                        className={twMerge('whitespace-nowrap', buttonClassName)}
                        size="smallNarrow"
                        type="button"
                        variant={buttonVariant}
                        onClick={toggleCollapse}
                    >
                        {isWithButtonIcons &&
                            (isCollapsed ? <ArrowIcon className="h-3 w-3" /> : <ArrowUpIcon className="h-3 w-3" />)}
                        <span>{isCollapsed ? buttonLabelMore : buttonLabelLess}</span>
                    </Button>
                </div>
            )}
        </>
    );
};

export default LimitHeightWrap;
